import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useRecoilState } from 'recoil'
import DefaultLayout from '../components/layouts/DefaultLayout'
import {
  Box,
  Text,
  Container,
  Heading,
  VStack,
  HStack,
  Button,
  Card,
  CardBody,
  Spinner,
  IconButton,
  useToast,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Badge,
  useBreakpointValue,
  Checkbox,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from '@chakra-ui/react'
import { FiEdit, FiTrash2, FiPlus, FiMessageSquare } from 'react-icons/fi'
import { authState } from '../atoms/auth'
import {
  getQnaList,
  createQna,
  updateQna,
  deleteQna,
} from '../services/apiService'
import dayjs from 'dayjs'

interface QnAPost {
  id: number
  userId: number
  username: string
  title: string
  question: string
  answer: string | null
  answeredBy: number | null
  answeredByUsername: string | null
  answeredAt: string | null
  status: string
  isPrivate: number
  canViewContent: boolean
  createdAt: string
  updatedAt: string | null
  isMine: boolean
}

const QnA: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [auth] = useRecoilState(authState)
  const toast = useToast()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const isMobile = useBreakpointValue({ base: true, md: false })

  const [qnas, setQnas] = useState<QnAPost[]>([])
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [limit] = useState(10)
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [isEditMode, setIsEditMode] = useState(false)
  const [editingQna, setEditingQna] = useState<QnAPost | null>(null)
  const [formTitle, setFormTitle] = useState('')
  const [formQuestion, setFormQuestion] = useState('')
  const [formIsPrivate, setFormIsPrivate] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const deleteDialog = useDisclosure()
  const [deletingQna, setDeletingQna] = useState<QnAPost | null>(null)
  const cancelRef = React.useRef<HTMLButtonElement>(null)

  const fetchQnas = async () => {
    setLoading(true)
    try {
      const { data: res } = await getQnaList(page, limit)
      if (res.statusCode === 200) {
        setQnas(res.data.qnas)
        setTotal(res.data.total)
        setTotalPages(res.data.totalPages)
      }
    } catch (error: any) {
      toast({
        title: t('오류'),
        description: error.response?.data?.message || t('질문 목록을 불러오는데 실패했습니다.'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchQnas()
  }, [page])

  const handleCreate = () => {
    if (!auth.id) {
      toast({
        title: t('로그인 필요'),
        description: t('질문을 작성하려면 로그인이 필요합니다.'),
        status: 'warning',
        duration: 3000,
        isClosable: true,
      })
      navigate('/signIn')
      return
    }
    setIsEditMode(false)
    setEditingQna(null)
    setFormTitle('')
    setFormQuestion('')
    setFormIsPrivate(false)
    onOpen()
  }

  const handleEdit = (qna: QnAPost) => {
    if (qna.userId !== auth.id) {
      toast({
        title: t('권한 없음'),
        description: t('수정 권한이 없습니다.'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
      return
    }

    if (qna.status === 'ANSWERED') {
      toast({
        title: t('수정 불가'),
        description: t('답변이 완료된 질문은 수정할 수 없습니다.'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
      return
    }

    setIsEditMode(true)
    setEditingQna(qna)
    setFormTitle(qna.title)
    setFormQuestion(qna.question)
    setFormIsPrivate(qna.isPrivate === 1)
    onOpen()
  }

  const handleDeleteClick = (qna: QnAPost) => {
    if (qna.userId !== auth.id) {
      toast({
        title: t('권한 없음'),
        description: t('삭제 권한이 없습니다.'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
      return
    }
    setDeletingQna(qna)
    deleteDialog.onOpen()
  }

  const handleDeleteConfirm = async () => {
    if (!deletingQna) return

    try {
      const { data: res } = await deleteQna(deletingQna.id)
      if (res.statusCode === 200) {
        toast({
          title: t('성공'),
          description: res.message,
          status: 'success',
          duration: 3000,
          isClosable: true,
        })
        deleteDialog.onClose()
        setDeletingQna(null)
        fetchQnas()
      }
    } catch (error: any) {
      toast({
        title: t('오류'),
        description: error.response?.data?.message || t('삭제에 실패했습니다.'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    }
  }

  const handleSubmit = async () => {
    if (!formTitle.trim() || !formQuestion.trim()) {
      toast({
        title: t('입력 오류'),
        description: t('제목과 질문 내용을 모두 입력해주세요.'),
        status: 'warning',
        duration: 3000,
        isClosable: true,
      })
      return
    }

    setSubmitting(true)
    try {
      if (isEditMode && editingQna) {
        const { data: res } = await updateQna(editingQna.id, formTitle, formQuestion, formIsPrivate)
        if (res.statusCode === 200) {
          toast({
            title: t('성공'),
            description: res.message,
            status: 'success',
            duration: 3000,
            isClosable: true,
          })
          onClose()
          fetchQnas()
        }
      } else {
        const { data: res } = await createQna(formTitle, formQuestion, formIsPrivate)
        if (res.statusCode === 200) {
          toast({
            title: t('성공'),
            description: res.message,
            status: 'success',
            duration: 3000,
            isClosable: true,
          })
          onClose()
          fetchQnas()
        }
      }
    } catch (error: any) {
      toast({
        title: t('오류'),
        description: error.response?.data?.message || t('작성에 실패했습니다.'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <DefaultLayout>
      <Container maxW="container.xl" py={10}>
        <VStack spacing={6} align="stretch">
          <HStack justify="space-between">
            <Heading size="lg">{t('Q&A')}</Heading>
            <Button
              leftIcon={<FiPlus />}
              colorScheme="brand"
              onClick={handleCreate}
            >
              {t('질문하기')}
            </Button>
          </HStack>

          {loading ? (
            <Box textAlign="center" py={10}>
              <Spinner size="xl" />
            </Box>
          ) : qnas.length === 0 ? (
            <Box textAlign="center" py={10}>
              <Text color="gray.500">{t('등록된 질문이 없습니다.')}</Text>
            </Box>
          ) : (
            <VStack spacing={4} align="stretch">
              {qnas.map((qna) => (
                <Card
                  key={qna.id}
                  cursor={qna.canViewContent ? 'pointer' : 'not-allowed'}
                  onClick={() => {
                    if (qna.canViewContent) {
                      navigate(`/qna/${qna.id}`)
                    } else {
                      toast({
                        title: t('접근 불가'),
                        description: t('비밀글은 작성자와 관리자만 볼 수 있습니다.'),
                        status: 'warning',
                        duration: 3000,
                        isClosable: true,
                      })
                    }
                  }}
                  borderLeft={qna.status === 'ANSWERED' ? '4px solid' : '4px solid'}
                  borderColor={qna.status === 'ANSWERED' ? 'green.500' : 'gray.300'}
                  opacity={qna.canViewContent ? 1 : 0.7}
                >
                  <CardBody>
                    <VStack align="stretch" spacing={3}>
                      <HStack justify="space-between">
                        <HStack spacing={2}>
                          <Heading size="md">{qna.title}</Heading>
                          {qna.isPrivate === 1 && (
                            <Badge colorScheme="purple">{t('비밀글')}</Badge>
                          )}
                          {qna.status === 'ANSWERED' ? (
                            <Badge colorScheme="green">{t('답변완료')}</Badge>
                          ) : (
                            <Badge colorScheme="gray">{t('대기중')}</Badge>
                          )}
                        </HStack>
                        {qna.isMine && qna.status === 'PENDING' && (
                          <HStack spacing={2}>
                            <IconButton
                              aria-label={t('수정')}
                              icon={<FiEdit />}
                              size="sm"
                              variant="ghost"
                              onClick={(e) => {
                                e.stopPropagation()
                                handleEdit(qna)
                              }}
                            />
                            <IconButton
                              aria-label={t('삭제')}
                              icon={<FiTrash2 />}
                              size="sm"
                              variant="ghost"
                              colorScheme="red"
                              onClick={(e) => {
                                e.stopPropagation()
                                handleDeleteClick(qna)
                              }}
                            />
                          </HStack>
                        )}
                      </HStack>
                      <Text color="gray.600" noOfLines={3} fontStyle={qna.canViewContent ? 'normal' : 'italic'}>
                        {qna.question}
                      </Text>
                      {qna.answer && qna.canViewContent && (
                        <Box
                          p={3}
                          bg="green.50"
                          borderRadius="md"
                          borderLeft="3px solid"
                          borderColor="green.500"
                        >
                          <HStack mb={2}>
                            <FiMessageSquare color="green" />
                            <Text fontSize="sm" fontWeight="bold" color="green.700">
                              {t('답변')}
                            </Text>
                          </HStack>
                          <Text fontSize="sm" color="gray.700" noOfLines={2}>
                            {qna.answer}
                          </Text>
                        </Box>
                      )}
                      <HStack justify="space-between" fontSize="sm" color="gray.500">
                        <Text>{qna.username}</Text>
                        <Text>{dayjs(qna.createdAt).format('YYYY-MM-DD HH:mm')}</Text>
                      </HStack>
                    </VStack>
                  </CardBody>
                </Card>
              ))}
            </VStack>
          )}

          {totalPages > 1 && (
            <HStack justify="center" spacing={2}>
              <Button
                size="sm"
                onClick={() => setPage(page - 1)}
                isDisabled={page === 1}
              >
                {t('이전')}
              </Button>
              <Text fontSize="sm">
                {page} / {totalPages}
              </Text>
              <Button
                size="sm"
                onClick={() => setPage(page + 1)}
                isDisabled={page >= totalPages}
              >
                {t('다음')}
              </Button>
            </HStack>
          )}
        </VStack>
      </Container>

      {/* 질문 작성/수정 모달 */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{isEditMode ? t('질문 수정') : t('질문 작성')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>{t('제목')}</FormLabel>
                <Input
                  value={formTitle}
                  onChange={(e) => setFormTitle(e.target.value)}
                  placeholder={t('질문 제목을 입력하세요')}
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>{t('질문 내용')}</FormLabel>
                <Textarea
                  value={formQuestion}
                  onChange={(e) => setFormQuestion(e.target.value)}
                  placeholder={t('질문 내용을 입력하세요')}
                  rows={10}
                />
              </FormControl>
              <FormControl>
                <Checkbox
                  isChecked={formIsPrivate}
                  onChange={(e) => setFormIsPrivate(e.target.checked)}
                >
                  {t('비밀글')}
                </Checkbox>
                <Text fontSize="sm" color="gray.500" mt={1}>
                  {t('비밀글로 설정하면 작성자와 관리자만 볼 수 있습니다.')}
                </Text>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              {t('취소')}
            </Button>
            <Button
              colorScheme="brand"
              onClick={handleSubmit}
              isLoading={submitting}
            >
              {isEditMode ? t('수정') : t('작성')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <AlertDialog
        isOpen={deleteDialog.isOpen}
        leastDestructiveRef={cancelRef}
        onClose={deleteDialog.onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {t('삭제 확인')}
            </AlertDialogHeader>

            <AlertDialogBody>
              {t('정말 삭제하시겠습니까?')}
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={deleteDialog.onClose}>
                {t('취소')}
              </Button>
              <Button colorScheme="red" onClick={handleDeleteConfirm} ml={3}>
                {t('삭제')}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </DefaultLayout>
  )
}

export default QnA
